import React, { memo } from 'react';
import { Card } from 'tea-component';
import Loading from '@gp/components/Loading';
import EllipsisTextBubble from '@gp/components/EllipsisTextBubble';
import MultipleLinesTextBubble from '@gp/components/MultipleLinesTextBubble';
import CommonSourceCard from '@gp/components/CommonSourceCard';
import {
  getObjNoData,
  toFirstUpperCase,
  getFormatNumPlus,
  getFormatNumPlusOfUSD,
  getRoundIntegerNum,
  getFormatPercentPlus,
} from '@gp/utils/common';
import { FIELDS, FIELDS1, FIELD_NAMES } from '@gp/utils/metricTips';
import moment from 'moment';
import './index.scss';
import CardSlot from '@/pages/globalpedia/components/CardSlot';
import DataTime from '@/pages/globalpedia/components/DataTime';

import DOLLAR_ICON from '@gp/assets/svg/gameInformation/dollar.svg';
import PERSON_ICON from '@gp/assets/svg/gameInformation/person.svg';

const DEFAULT_PROPS: KeyInfomationPropsType = {
  loading: true,
  data: null,
  dataTime: '',
};

function KeyInfomation(props: KeyInfomationPropsType) {
  const { loading, data, dataTime } = props;

  const {
    studio_type,
    mgt_team,
    category,
    gross_revenue_ytm,
    profit_ytm,
    total_fulltime_employee,
  } = data || {};

  const lastYear =
    moment(dataTime).isValid() && dataTime
      ? moment(dataTime).subtract(1, 'y').format('YYYY')
      : '-';
  const currentYear =
    moment(dataTime).isValid() && dataTime
      ? moment(dataTime).format('YYYY')
      : '-';

  return (
    <CardSlot
      className="dbg-details-overview-key-infomation"
      title={
        <div className="dbg-details-overview-key-infomation-title">
          Key Infomation
          {(!loading && dataTime) && (
            <DataTime
              dateType="Month"
              date={dataTime}
              className="dbg-details-overview-key-infomation-time"
            />
          )}
        </div>
      }
    >
      {loading && <Loading loading style={{ height: 200 }} />}
      {loading || (
        <>
          <div className="dbg-infomation-text">
            <div className="infomation-text-flex">
              <label className="infomation-text-flex-label">Studio Type:</label>
              <EllipsisTextBubble
                className="infomation-text-flex-text"
                text={toFirstUpperCase(studio_type || '') || '-'}
              />
            </div>
            <div className="infomation-text-flex">
              <label className="infomation-text-flex-label">
                Management Team:
              </label>
              <EllipsisTextBubble
                className="infomation-text-flex-text"
                text={toFirstUpperCase(mgt_team || '') || '-'}
              />
            </div>
            <div className="infomation-text-flex">
              <label className="infomation-text-flex-label">Category:</label>
              <EllipsisTextBubble
                className="infomation-text-flex-text"
                text={toFirstUpperCase(category || '') || '-'}
              />
            </div>
          </div>
          <div className="key-infomation-content">
            {/* 第一列 */}
            <div className="key-infomation-col">
              <CommonSourceCard
                isTitleMultipleLinesTextBubble
                multipleLinesTextFields={FIELDS1}
                multipleLinesTextFieldNames={FIELD_NAMES}
                multipleLinesTextData={{
                  definition: `
                    Actual total sales amount to the month.
                    <br />
                    Noted: This amount does not deduct steam/Epic platform share, and also include the sales amount of retail activation code (e.g. Humble Bundle, Heybox, etc.).
                  `,
                  source: 'Studio Reporting Package',
                  updateCadence: 'Monthly',
                }}
                isNoData={getObjNoData(gross_revenue_ytm)}
                icon={
                  <img
                    src={DOLLAR_ICON}
                    alt=""
                  />
                }
                title="Gross Revenue YTM"
                value={getFormatNumPlusOfUSD(gross_revenue_ytm?.Total || '') || '-'}
                percentV={gross_revenue_ytm?.Ratio}
                // @ts-ignore
                isPercentage={
                  !Number.isNaN(Number.parseFloat(gross_revenue_ytm?.Ratio)) &&
                  Number.parseFloat(gross_revenue_ytm?.Ratio) !== 0
                }
                slot={
                  <div className="source-card-footer-slot">
                    <div className="footer-item">
                      <label className="item-title">{lastYear} Actual</label>
                      <span className="item-value">
                        {getFormatNumPlusOfUSD(
                          gross_revenue_ytm?.LastYearActual || '',
                        ) || '-'}
                      </span>
                    </div>
                    <div className="footer-item">
                      <label className="item-title">{currentYear} Budget</label>
                      <span className="item-value">
                        {getFormatNumPlusOfUSD(
                          gross_revenue_ytm?.ThisYearBudget || '',
                        ) || '-'}
                      </span>
                    </div>
                    <div className="footer-item">
                      <label className="item-title">Completion%</label>
                      <span className="item-value">
                        {getFormatPercentPlus(
                          gross_revenue_ytm?.Completion || '',
                          1,
                        ) || '-'}
                      </span>
                    </div>
                  </div>
                }
              />
            </div>
            {/* 第二列 */}
            <div className="key-infomation-col" style={{ marginLeft: 20 }}>
              <CommonSourceCard
                isTitleMultipleLinesTextBubble
                multipleLinesTextFields={FIELDS1}
                multipleLinesTextFieldNames={FIELD_NAMES}
                multipleLinesTextData={{
                  definition: 'Net profit to the month, excluding all below line items, such as interest expenses and taxes.',
                  source: 'Studio Reporting Package',
                  updateCadence: 'Monthly',
                }}
                isNoData={getObjNoData(profit_ytm)}
                icon={
                  <img
                    src={DOLLAR_ICON}
                    alt=""
                  />
                }
                title="Profit YTM"
                value={getFormatNumPlusOfUSD(profit_ytm?.Total || '') || '-'}
                percentV={profit_ytm?.Ratio}
                // @ts-ignore
                isPercentage={
                  !Number.isNaN(Number.parseFloat(profit_ytm?.Ratio)) &&
                  Number.parseFloat(profit_ytm?.Ratio) !== 0
                }
                slot={
                  <div className="source-card-footer-slot">
                    <div className="footer-item">
                      <label className="item-title">{lastYear} Actual</label>
                      <span className="item-value">
                        {getFormatNumPlusOfUSD(profit_ytm?.LastYearActual || '') || '-'}
                      </span>
                    </div>
                    <div className="footer-item">
                      <label className="item-title">{currentYear} Budget</label>
                      <span className="item-value">
                        {getFormatNumPlusOfUSD(profit_ytm?.ThisYearBudget || '') || '-'}
                      </span>
                    </div>
                    <div className="footer-item">
                      <label className="item-title">Completion%</label>
                      <span className="item-value">
                        {getFormatPercentPlus(
                          profit_ytm?.Completion || '',
                          1,
                        ) || '-'}
                      </span>
                    </div>
                  </div>
                }
              />
            </div>
            {/* 第三列 */}
            <div className="key-infomation-col" style={{ marginLeft: 20 }}>
              <CommonSourceCard
                isTitleMultipleLinesTextBubble
                multipleLinesTextFields={FIELDS1}
                multipleLinesTextFieldNames={FIELD_NAMES}
                multipleLinesTextData={{
                  definition: 'Total in house full-time employee of all portfolio studios.',
                  source: 'Studio Reporting Package',
                  updateCadence: 'Monthly',
                }}
                isNoData={getObjNoData(total_fulltime_employee)}
                icon={
                  <img
                    src={PERSON_ICON}
                    alt=""
                  />
                }
                title="Total Fulltime Employer"
                value={getRoundIntegerNum(total_fulltime_employee?.total)}
                percentV={total_fulltime_employee?.ratio}
                // @ts-ignore
                isPercentage={
                  !Number.isNaN(
                    Number.parseFloat(total_fulltime_employee?.ratio),
                  ) && Number.parseFloat(total_fulltime_employee?.ratio) !== 0
                }
                slot={
                  <div className="source-card-footer-slot">
                    <div className="footer-item">
                      <label className="item-title">{lastYear} FTE</label>
                      <span className="item-value">
                        {getRoundIntegerNum(
                          total_fulltime_employee?.last_year_FTE,
                        )}
                      </span>
                    </div>
                    <div className="footer-item">
                      <label className="item-title">New Hiring This Year</label>
                      <span className="item-value">
                        {getRoundIntegerNum(
                          total_fulltime_employee?.this_year_new_hiring,
                        )}
                      </span>
                    </div>
                  </div>
                }
              />
            </div>
          </div>
        </>
      )}
    </CardSlot>
  );
}

KeyInfomation.defaultProps = DEFAULT_PROPS;

export default memo(KeyInfomation);
